"use client";

import { useAuiState } from "@assistant-ui/react";
import {
  CircleChevronRightIcon,
  ChevronRightIcon,
  LoaderCircleIcon
} from "lucide-react";
import { useMemo, useState } from "react";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from "@/agents-v2/components/ui/collapsible";
import { asRecord, parseMaybeJson } from "@/agents-v2/utils/unknown";
import { cn } from "@/agents-v2/utils/cn";

export const WRITE_TODOS_TOOL_NAME = "write_todos";

type TodoStatus = "pending" | "in_progress" | "completed";

type ThreadTodo = {
  content: string;
  status: TodoStatus;
};

const normaliseStatus = (value: unknown): TodoStatus => {
  if (value === "in_progress") return "in_progress";
  if (value === "completed" || value === "complete" || value === "done") {
    return "completed";
  }
  return "pending";
};

const parseTodos = (value: unknown): ThreadTodo[] => {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => {
      const record = asRecord(item);
      const content = record.content;
      if (typeof content !== "string" || !content.trim()) return null;

      return {
        content: content.trim(),
        status: normaliseStatus(record.status)
      };
    })
    .filter((todo): todo is ThreadTodo => todo !== null);
};

// Custom Streamline "Check Circle" used as the completed tick box; strokes
// inherit the current text colour via currentColor.
function CheckCircleIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="-0.315 -0.315 15 15"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={0.63}
      className={className}
      aria-hidden="true"
    >
      <path d="m10.7775 4.19125 -3.820025 5.185175a0.59875 0.59875 0 0 1 -0.40715 0.23950000000000002 0.59875 0.59875 0 0 1 -0.44906250000000003 -0.1257375L3.5925000000000002 7.484375" />
      <path d="M0.299375 7.1850000000000005a6.885625 6.885625 0 1 0 13.77125 0 6.885625 6.885625 0 1 0 -13.77125 0" />
    </svg>
  );
}

function TodoStatusIcon({ status }: { status: TodoStatus }) {
  if (status === "completed") {
    return (
      <CheckCircleIcon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
    );
  }

  if (status === "in_progress") {
    return (
      <LoaderCircleIcon className="mt-0.5 size-4 shrink-0 animate-spin text-muted-foreground" />
    );
  }

  return (
    <CircleChevronRightIcon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
  );
}

function TodoRow({ todo, index }: { todo: ThreadTodo; index: number }) {
  const isCompleted = todo.status === "completed";

  return (
    <li
      data-todo-index={index}
      className="flex w-full min-w-0 items-start gap-2 px-2 py-1"
    >
      <TodoStatusIcon status={todo.status} />
      <span
        className={cn(
          "block min-w-0 flex-1 break-words text-foreground/80",
          isCompleted &&
            "text-muted-foreground line-through decoration-[0.5px]"
        )}
      >
        {todo.content}
      </span>
    </li>
  );
}

function PlanProgress({
  completed,
  total
}: {
  completed: number;
  total: number;
}) {
  if (total <= 0 || completed >= total) return null;

  const radius = 7;
  const circumference = 2 * Math.PI * radius;
  const progress = Math.max(0, Math.min(1, completed / total));

  return (
    <svg
      aria-label={`${completed} of ${total} plan steps complete`}
      className="size-4 shrink-0 -rotate-90 text-muted-foreground"
      role="img"
      viewBox="0 0 18 18"
    >
      <circle
        cx="9"
        cy="9"
        fill="none"
        r={radius}
        stroke="currentColor"
        strokeOpacity="0.18"
        strokeWidth="2"
      />
      <circle
        cx="9"
        cy="9"
        fill="none"
        r={radius}
        stroke="currentColor"
        strokeDasharray={circumference}
        strokeDashoffset={circumference * (1 - progress)}
        strokeLinecap="round"
        strokeWidth="2"
      />
    </svg>
  );
}

// Renders the agent's plan inline in the message stream, anchored to the
// group-tool block that holds the FIRST write_todos call in the message. The
// card reflects the latest plan snapshot in that message, so it crosses items
// off in place as the agent revises the plan instead of popping up over the
// composer. `indices` are the part indices owned by the surrounding group.
export function ThreadPlan({ indices }: { indices: readonly number[] }) {
  const [open, setOpen] = useState(true);
  const messageParts = useAuiState((state) => state.message.parts);
  // The plan is only "live" while this (latest) message's run is still running.
  // Once it settles, a todo left in_progress — the agent commonly ends a turn
  // without emitting a final all-completed write_todos — must stop spinning
  // instead of loading forever (MYT-1776).
  const runActive = useAuiState(
    (state) => state.thread.isRunning && state.message.isLast
  );

  const { todos, firstWriteTodosIndex } = useMemo(() => {
    let latest: ThreadTodo[] = [];
    let firstIndex = -1;

    messageParts.forEach((part, index) => {
      const record = asRecord(part);
      if (
        record.type !== "tool-call" ||
        record.toolName !== WRITE_TODOS_TOOL_NAME
      ) {
        return;
      }

      if (firstIndex === -1) firstIndex = index;

      const fromArgs = parseTodos(asRecord(record.args).todos);
      const snapshot =
        fromArgs.length > 0
          ? fromArgs
          : parseTodos(asRecord(parseMaybeJson(record.argsText)).todos);
      // Keep the most recent non-empty snapshot so live status updates win.
      if (snapshot.length > 0) latest = snapshot;
    });

    return { todos: latest, firstWriteTodosIndex: firstIndex };
  }, [messageParts]);

  // When the run is no longer active for this message, treat any lingering
  // in_progress todo as completed so the row stops spinning and the progress
  // ring settles (MYT-1776).
  const displayTodos = useMemo(
    () =>
      runActive
        ? todos
        : todos.map((todo) =>
            todo.status === "in_progress"
              ? { ...todo, status: "completed" as const }
              : todo
          ),
    [todos, runActive]
  );

  if (displayTodos.length === 0) return null;
  // Only the group that owns the first write_todos call renders the card, so a
  // single plan shows per turn even when the agent revises it repeatedly.
  if (firstWriteTodosIndex === -1 || !indices.includes(firstWriteTodosIndex)) {
    return null;
  }

  const completedCount = displayTodos.filter(
    (todo) => todo.status === "completed"
  ).length;

  return (
    <div data-slot="aui_thread-plan" className="my-3">
      <Collapsible
        open={open}
        onOpenChange={setOpen}
        className="rounded-xl border bg-muted/35 px-2 pt-2 pb-2 shadow-sm backdrop-blur-sm"
      >
        <CollapsibleTrigger
          type="button"
          className="flex w-full items-center justify-between gap-2 rounded-sm px-1 pb-1 text-sm text-muted-foreground outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          <span className="flex min-w-0 items-center gap-1.5">
            <ChevronRightIcon
              className={cn(
                "size-3 shrink-0 transition-transform",
                open && "rotate-90"
              )}
            />
            <span className="font-medium text-foreground/70">Plan</span>
          </span>
          <PlanProgress completed={completedCount} total={displayTodos.length} />
        </CollapsibleTrigger>
        {open ? <div className="-mx-2 border-border/60 border-b" /> : null}
        <CollapsibleContent
          forceMount
          className={cn(
            "grid overflow-hidden transition-[grid-template-rows,opacity] duration-200 ease-out",
            "data-[state=closed]:grid-rows-[0fr] data-[state=closed]:opacity-0",
            "data-[state=open]:grid-rows-[1fr] data-[state=open]:opacity-100",
            "data-[state=closed]:pointer-events-none"
          )}
        >
          <div className="min-h-0 overflow-hidden">
            <ol className="min-w-0 space-y-0.5 py-1 text-sm">
              {displayTodos.map((todo, index) => (
                <TodoRow key={index} todo={todo} index={index} />
              ))}
            </ol>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
